import React, { createContext, useState, useContext } from 'react';
import FeedbackContext from './FeedbackContext';
import Proptypes from 'prop-types';
import { useBebackApi } from '../hooks/useBebackApi';

const StoreContext = createContext();

const StoreProvider = ({ children }) => {
  const { setIsLoading, setShowErrorAlert } = useContext(FeedbackContext);
  const { getStoreList, updateSites } = useBebackApi()

  const [storeList, setStoreList] = useState([]);
  const [storeId, setStoreId] = useState('1');
  const [label, setLabel] = useState('');
  const [address, setAdress] = useState('');
  const [time_zone, setTime_zone] = useState('us/mountain');

  // Settings
  const [promisedTimeSms, setPromisedTimeSms] = useState(false);
  const [vehicleReadySms, setVehicleReadySms] = useState(false);
  const [nextAppt, setNextAppt] = useState(false);
  const [courtesyLyft, setCourtesyLyft] = useState(false);
  const [smartPromiseTime, setSmartPromiseTime] = useState(false);
  const [numberOfBays, setNumberOfBays] = useState(0);
  const [multipleSms, setMultipleSms] = useState(false);
  const [globalBeback, setGlobalBeback] = useState(false);
  const [promiseTimeAppointments, setPromiseTimeAppointments] = useState(false);

  const updateStore = async (storeId, isAdmin) => {
    const response = await getStoreList(storeId);
    
    if (response?.error || response?.length !== 1) {
      // Store doesn't exist
      setShowErrorAlert(response.error);
      if (isAdmin) {
        resetStore();
      } else {
        const defaultStore =
          (await localStorage.getItem('sessionStoreNo')) ||
          (await localStorage.getItem('defaultStore'));
        setStoreId(defaultStore);
      }
      setIsLoading(false);
      return;
    }
    
    const storeFromBack = response[0];
    localStorage.setItem('currentStoreId', storeFromBack.value)
    
    storeFromBack.courtesy_lyft !== undefined && setCourtesyLyft(+storeFromBack.courtesy_lyft);
    storeFromBack.value !== undefined && setStoreId(storeFromBack.value);
    storeFromBack.label !== undefined && setLabel(storeFromBack.label);
    storeFromBack.store_address !== undefined && setAdress(storeFromBack.store_address);
    storeFromBack.time_zone !== undefined && setTime_zone(storeFromBack.time_zone);
    
    storeFromBack.promisedSms !== undefined && setPromisedTimeSms(+storeFromBack.promisedSms);
    storeFromBack.readySms !== undefined && setVehicleReadySms(+storeFromBack.readySms);
    storeFromBack.appt_calendar !== undefined && setNextAppt(+storeFromBack.appt_calendar);
    storeFromBack.smart_promise_time !== undefined && setSmartPromiseTime(+storeFromBack.smart_promise_time);
    storeFromBack.number_of_bays !== undefined && setNumberOfBays(parseInt(storeFromBack.number_of_bays));
    storeFromBack.multiple_sms !== undefined && setMultipleSms(+storeFromBack.multiple_sms);
    storeFromBack.global_beback !== undefined && setGlobalBeback(+storeFromBack.global_beback);
    storeFromBack.promise_time_appointments !== undefined && setPromiseTimeAppointments(+storeFromBack.promise_time_appointments);
  };

  const resetStore = () => {
    setStoreId('1');
    setLabel('');
    setAdress('');
    setTime_zone('us/mountain');

    setPromisedTimeSms(false);
    setVehicleReadySms(false);
    setNextAppt(false);
    setCourtesyLyft(false);
    setSmartPromiseTime(false);
    setMultipleSms(false);
    setGlobalBeback(false);
    setPromiseTimeAppointments(false);
  };

  const fetchStores = async (isAdmin) => {
    setIsLoading(true);
    setShowErrorAlert(false);
    const response = await getStoreList(1);

    if (response.error || response?.length <= 1) {
      // There was an error, reset the store
      setShowErrorAlert(response.error);
      if (isAdmin) {
        resetStore();
      } else {
        const defaultStore =
          (await localStorage.getItem('sessionStoreNo')) ||
          (await localStorage.getItem('defaultStore'));
        setStoreId(defaultStore);
      }
      setIsLoading(false);
      return;
    }

    const formattedStores = response.map(item => ({
      ...item,
      value: parseInt(item.value, 10),   //parse store number from string to int
    }))

    setStoreList(formattedStores);
    setIsLoading(false);
  };

  const updateSetting = async (
    setting,
    param,
    username,
    selectedOption,
    selectedStores
  ) => {
    let params;

    switch (selectedOption) {
      case 'none':
        params = [{ all: 0 }];
        break;
      case 'all':
        params = [{ all: 1 }];
        break;
      case 'list':
        params = storeList
          .filter((store) => !selectedStores.some(selectedStore => store.value === selectedStore.value))
          .map((store) => {
            return { SiteNo: store.value, [param]: 0 };
          });
        params = params.concat(
          storeList
            .filter((store) => selectedStores.some(selectedStore => store.value === selectedStore.value))
            .map((store) => {
              return { SiteNo: store.value, [param]: 1 };
            })
        );
        break;
      default:
        console.log('Invalid selected option', selectedOption);
        return;
    }
    
    try {
      await updateSites(username, param, params);

      if (+storeId !== 1) {
        const response = await getStoreList(storeId);
        
        if (response.length === 1) {
          await updateStore(response[0].value, true);
        }
      }
      const responseGetStores = await getStoreList(1);
      if (responseGetStores?.length > 1) {
        setStoreList(responseGetStores);
        return responseGetStores.filter((store) => +store[setting]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const contextValue = {
    storeId,
    fetchStores,
    label,
    address,
    time_zone,
    promisedTimeSms,
    vehicleReadySms,
    nextAppt,
    courtesyLyft,
    smartPromiseTime,
    storeList,
    setStoreList,
    updateStore,
    resetStore,
    updateSetting,
    numberOfBays,
    multipleSms,
    globalBeback,
    promiseTimeAppointments
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

StoreProvider.propTypes = {
  children: Proptypes.element
}

export { StoreContext as default, StoreProvider };
